/**
 * FlyoutDrawer — shared persistent right-side drawer used by all V2 CRUD pages.
 *
 * Provides: header (title + close button), scrollable content area, footer area.
 * Extracted from V2Users, V2Roles, V2Sites, V2Views, V2Connectors,
 * and V2SummaryTemplates to eliminate ~60 duplicated lines per page.
 */

import { Box, Drawer } from "@mui/material";
import { DRAWER_WIDTH, closeButtonSx, drawerHeaderSx, drawerTitleSx, drawerScrollAreaSx } from "./crudStyles";

// ---------------------------------------------------------------------------
// Close button (X icon) — shared across all flyout headers
// ---------------------------------------------------------------------------

function CloseButton({ onClick }: Readonly<{ onClick: () => void }>) {
  return (
    <Box component="button" onClick={onClick} sx={closeButtonSx}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 16, height: 16 }}>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// FlyoutDrawer component
// ---------------------------------------------------------------------------

interface FlyoutDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly children: React.ReactNode;
  /** Footer element rendered below the scrollable content */
  readonly footer?: React.ReactNode;
}

export default function FlyoutDrawer({
  open,
  onClose,
  title,
  children,
  footer,
}: FlyoutDrawerProps) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          bgcolor: "var(--bg-surface)",
          borderLeft: "1px solid var(--border-mid)",
          color: "var(--text-primary)",
          boxShadow: "-8px 0 24px rgba(0,0,0,0.2)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Header */}
        <Box sx={drawerHeaderSx}>
          <Box sx={drawerTitleSx}>{title}</Box>
          <CloseButton onClick={onClose} />
        </Box>

        {/* Scrollable content */}
        <Box sx={drawerScrollAreaSx}>{children}</Box>

        {/* Footer */}
        {footer}
      </Box>
    </Drawer>
  );
}
